/**
 * The second column's default view (owner direction #700, 2026-09-29): compact text reports
 * instead of terminals. What is running now and its latest progress, what needs the owner, and
 * what finished recently - all read from Brunel's canonical rows the room already holds.
 */
import type { ReactElement } from 'react';
import type { FinishedRun, RunProgress, RunningTask } from '../lib/data';
import type { CockpitWorkerCard } from '../lib/cockpit-view';
import { runTaskTitle } from '../lib/finished';
import { MERGED_AWAITING_OWNER, type OwnerGateRunView } from '../lib/owner-gate-waiting';
import { presentRecentRuns } from '../lib/recent-run-supersession';
import { latestProgressForRun } from './RunProgressLine';
import { progressStep, runTurnView } from '../lib/run-turn';

/** Where the header's failure count opens: the same failures, listed. */
export const CURRENT_FAILURES_ID = 'reports-current-failures';

const RECENT_RESULTS = 8;

/** A Worker belongs on the board while it has a job, needs the owner, or is being looked at. */
export function workerHasJob(card: CockpitWorkerCard, running: readonly RunningTask[]): boolean {
  if (running.some((run) => run.worker_number === card.workerNumber)) return true;
  if (card.attention !== null) return true;
  return !['idle', 'offline', 'parked', 'closed', 'retired', 'unknown'].includes(card.status.toLowerCase());
}

function since(iso: string | null, nowMs: number): string | null {
  if (iso === null) return null;
  const ms = nowMs - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours} h ${minutes % 60} min` : `${Math.floor(hours / 24)} d`;
}

function worker(number: number | null, name: string | null): string {
  if (number === null) return 'No Worker';
  return name !== null && name.trim() !== '' && name.trim() !== `Worker ${number}` ? `Worker ${number} · ${name}` : `Worker ${number}`;
}

/** Worker, computer and project for a Running now card, naming the computer once. */
export function runningMeta(run: Pick<RunningTask, 'worker_number' | 'worker_name' | 'node_name' | 'project_name'>): string {
  const who = worker(run.worker_number, run.worker_name);
  const computer = run.node_name.trim();
  const parts = [who];
  if (computer !== '' && !who.toLowerCase().includes(computer.toLowerCase())) parts.push(computer);
  if (run.project_name.trim() !== '') parts.push(run.project_name);
  return parts.join(' · ');
}

export function ReportsPanel({
  running, progress, results, titles, needsYou, nowMs, onOpenAttention, ownerGateViews, failures = [],
}: {
  readonly running: readonly RunningTask[];
  readonly progress: readonly RunProgress[];
  readonly results: readonly FinishedRun[];
  readonly titles: ReadonlyMap<string, string>;
  readonly needsYou: number;
  readonly nowMs: number;
  readonly onOpenAttention: () => void;
  readonly ownerGateViews?: ReadonlyMap<string, OwnerGateRunView>;
  /** The current failures the header counts (lib/current-failures), newest first. */
  readonly failures?: readonly FinishedRun[];
}): ReactElement {
  const views = presentRecentRuns(results);
  const recent = [...results]
    .sort((a, b) => Date.parse(b.reported_at) - Date.parse(a.reported_at))
    .slice(0, RECENT_RESULTS);
  return (
    <section className="reports-panel stack" aria-label="Reports">
      {needsYou > 0 && (
        <button type="button" className="reports-needs-you" onClick={onOpenAttention}>
          <strong>{needsYou} {needsYou === 1 ? 'thing needs' : 'things need'} you</strong>
          <span className="muted">Open</span>
        </button>
      )}
      {failures.length > 0 && (
        <div className="stack" id={CURRENT_FAILURES_ID} tabIndex={-1} aria-label="Failed today">
          <h3 className="reports-heading">Failed today <small className="muted">({failures.length})</small></h3>
          <ul className="reports-list">
            {failures.map((run) => {
              const summary = run.summary.trim();
              return (
                <li key={run.assignment_id} className="reports-item" data-outcome="failed">
                  <div className="row spread">
                    <strong>✗ {runTaskTitle(run, titles)}</strong>
                    <small className="muted">{since(run.reported_at, nowMs) ?? ''}</small>
                  </div>
                  <small className="muted">{worker(run.worker_number, null)}</small>
                  {summary !== '' && <p className="reports-summary">{summary.slice(0, 280)}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="stack">
        <h3 className="reports-heading">Running now <small className="muted">({running.length})</small></h3>
        {running.length === 0 ? <p className="muted">Nothing is running. Workers start when a job needs them.</p> : (
          <ul className="reports-list">
            {running.map((run) => {
              const step = progressStep(latestProgressForRun(run, progress)?.tail);
              const turn = runTurnView(run, progress, nowMs);
              return (
                <li key={run.assignment_id} className="reports-item" data-quiet={turn.warning !== null ? 'true' : 'false'}>
                  <div className="row spread">
                    <strong>{run.title}</strong>
                    <small className="muted">{since(run.started_at, nowMs) ?? ''}</small>
                  </div>
                  <small className="muted">{runningMeta(run)}</small>
                  <small className="muted reports-turn">{turn.label}</small>
                  {turn.warning !== null && <small className="reports-quiet" role="status">{turn.warning}</small>}
                  {step !== null && <p className="reports-progress">{step}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="stack">
        <h3 className="reports-heading">Recent runs</h3>
        {recent.length === 0 ? <p className="muted">No finished work yet.</p> : (
          <ul className="reports-list">
            {recent.map((run) => {
              const view = views.get(run.assignment_id);
              const awaiting = ownerGateViews?.get(run.assignment_id)?.awaitingOwner === true;
              const summary = (view?.summary ?? run.summary).trim();
              return (
                <li key={run.assignment_id} className="reports-item" data-outcome={awaiting ? 'awaiting-owner' : run.success ? 'ok' : 'failed'} data-superseded={view?.superseded ? 'true' : 'false'}>
                  <div className="row spread">
                    <strong>{awaiting ? MERGED_AWAITING_OWNER : run.success ? '✓' : '✗'} {runTaskTitle(run, titles)}</strong>
                    <small className="muted">{since(run.reported_at, nowMs) ?? ''}</small>
                  </div>
                  <small className="muted">{worker(run.worker_number, null)}</small>
                  {view?.supersededByLabel !== null && view?.supersededByLabel !== undefined && (
                    <small className="muted">{view.supersededByLabel}</small>
                  )}
                  {summary !== '' && <p className="reports-summary">{summary.slice(0, 280)}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
